import React from 'react';

import ResponseScaleProvider from '../response/ResponseScaleProvider';
import ClarifyingQuestion from '../response/blocks/ClarifyingQuestion';
import GovernedAnalysis from '../response/blocks/GovernedAnalysis';
import GrowthPlannerHandoff from '../response/blocks/GrowthPlannerHandoff';
import MultiAnswerPanel from '../response/blocks/MultiAnswerPanel';
import RenewalReviewRequest from '../response/blocks/RenewalReviewRequest';
import ReportGenerated from '../response/blocks/ReportGenerated';
import SupportTicketForm from '../response/blocks/SupportTicketForm';
import SupportTicketRecord from '../response/blocks/SupportTicketRecord';
import SupportTicketTable from '../response/blocks/SupportTicketTable';
import WeeklyUpdateReport from '../response/blocks/WeeklyUpdateReport';
import { ResponseVariant } from '../response/responseScale';
import { RESPONSE_BLOCK_TYPES, isKnownSupportBlock } from '../supportFormat';
import { SupportBlock, SupportBlockType } from '../types';
import {
  ContractSummary,
  FeatureSubscriptions,
  MultiAnswer,
  OutOfScope,
  SupportTicketDraft
} from './BubbleBlocks';
import {
  ActionRow,
  Banner,
  BulletList,
  Chart,
  Confidence,
  KeyValue,
  MetricTiles,
  PanelShell,
  Table,
  Text
} from './GenericBlocks';
import GovernedState from './GovernedState';
import {
  IncidentsMaintenance,
  RenewalReviewForm,
  SupportTicketDetail,
  SupportTicketList,
  WeeklyUpdate
} from './PanelBlocks';
import { Stack } from './supportStyles';

// The 6098:* frames draw these as panels beside the avatar; the rest sit in the chat bubble. The
// page response types draw their own intro and panel, so none takes the bubble.
const PANEL_TYPES: ReadonlySet<SupportBlockType> = new Set<SupportBlockType>([
  ...RESPONSE_BLOCK_TYPES,
  'weekly_update',
  'incidents_maintenance',
  'support_ticket_list',
  'support_ticket_detail',
  'renewal_review_form',
  'renewal_review_success',
  'panel',
  'chart',
  'table',
  'banner',
  'key_value',
  'metric_tiles'
]);

export const knownSupportBlocks = (blocks: unknown[] | null | undefined): SupportBlock[] =>
  Array.isArray(blocks) ? blocks.filter(isKnownSupportBlock) : [];

export const drawsSupportPanel = (blocks: SupportBlock[]): boolean =>
  blocks.length > 0 && PANEL_TYPES.has(blocks[0].type);

const renderBlock = (block: SupportBlock): React.ReactNode => {
  switch (block.type) {
    case 'support_ticket_form':
      return <SupportTicketForm block={block} />;
    case 'clarifying_question':
      return <ClarifyingQuestion block={block} />;
    case 'support_ticket_table':
      return <SupportTicketTable block={block} />;
    case 'support_ticket_record':
      return <SupportTicketRecord block={block} />;
    case 'renewal_review_request':
      return <RenewalReviewRequest block={block} />;
    case 'weekly_update_report':
      return <WeeklyUpdateReport block={block} />;
    case 'multi_answer_panel':
      return <MultiAnswerPanel block={block} />;
    case 'report_generated':
      return <ReportGenerated block={block} />;
    case 'governed_analysis':
      return <GovernedAnalysis block={block} />;
    case 'growth_planner_handoff':
      return <GrowthPlannerHandoff block={block} />;
    case 'feature_subscriptions':
      return <FeatureSubscriptions block={block} />;
    case 'support_ticket_draft':
      return <SupportTicketDraft block={block} />;
    case 'out_of_scope':
      return <OutOfScope block={block} />;
    case 'multi_answer':
      return <MultiAnswer block={block} />;
    case 'contract_summary':
      return <ContractSummary block={block} />;
    case 'weekly_update':
      return <WeeklyUpdate block={block} />;
    case 'incidents_maintenance':
      return <IncidentsMaintenance block={block} />;
    case 'support_ticket_list':
      return <SupportTicketList block={block} />;
    case 'support_ticket_detail':
      return <SupportTicketDetail block={block} />;
    case 'renewal_review_form':
      return <RenewalReviewForm block={block} />;
    // Alias: drawn as a success banner with no bullets or actions.
    case 'renewal_review_success':
      return (
        <Banner block={{ type: 'banner', tone: 'success', title: block.title, body: block.body }} />
      );
    case 'text':
      return <Text block={block} />;
    case 'chart':
      return <Chart block={block} />;
    case 'key_value':
      return <KeyValue block={block} />;
    case 'table':
      return <Table block={block} />;
    case 'metric_tiles':
      return <MetricTiles block={block} />;
    case 'bullet_list':
      return <BulletList block={block} />;
    case 'banner':
      return <Banner block={block} />;
    case 'action_row':
      return <ActionRow block={block} />;
    case 'confidence':
      return <Confidence block={block} />;
    case 'panel':
      return (
        <PanelShell heading={block.heading} info={block.tone === 'info'}>
          {knownSupportBlocks(block.blocks).map((child, index) => (
            // Blocks carry no id; their position in the panel is their identity.
            // eslint-disable-next-line react/no-array-index-key
            <div key={`${child.type}-${index}`} data-testid={`orbit-support-block-${child.type}`}>
              <GovernedState meta={child}>{renderBlock(child)}</GovernedState>
            </div>
          ))}
        </PanelShell>
      );
    default:
      return null;
  }
};

const OrbitSupportBlocks: React.FC<{
  blocks: SupportBlock[];
  /** The side panel draws the response types at its own, smaller scale. */
  variant?: ResponseVariant;
}> = ({ blocks, variant = 'page' }) => {
  const known = knownSupportBlocks(blocks);
  if (known.length === 0) {
    return null;
  }
  return (
    <ResponseScaleProvider variant={variant}>
      <Stack $gap={12} data-testid="orbit-support-blocks" data-variant={variant}>
        {known.map((block, index) => (
          // Blocks carry no id; their position in the reply is their identity.
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${block.type}-${index}`} data-testid={`orbit-support-block-${block.type}`}>
            <GovernedState meta={block}>{renderBlock(block)}</GovernedState>
          </div>
        ))}
      </Stack>
    </ResponseScaleProvider>
  );
};

export default OrbitSupportBlocks;
